"use client";

import { useEffect } from "react";

function parseHex(hex: string): [number, number, number] {
  let clean = hex.replace(/^#/, "").trim();
  if (clean.length === 3) {
    clean = clean
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const num = parseInt(clean, 16);
  if (isNaN(num) || clean.length !== 6) {
    throw new Error(`Ungültiger Hex-Code: ${hex}`);
  }
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

export function WebMcpProvider() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Detect WebMCP surface on document or navigator (Chrome Canary / Agentic Chrome)
    const ctx = (document as any).modelContext || (navigator as any).modelContext;

    const tools = [
      {
        name: "search_digital_garden",
        description:
          "Durchsucht den Digital Garden von Lukas Kopischke nach Entwickler-Cheat-Sheets, Architektur-Patterns, Leitfäden und Best Practices.",
        inputSchema: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description:
                "Suchbegriff, Frage oder Thema (z. B. 'Git Rebase vs Merge', 'INP', 'WCAG', 'MDX', 'Embeddings')",
            },
            category: {
              type: "string",
              description:
                "Optionale Filterung nach Kategorie (z. B. 'Web Standards', 'AI & Architecture', 'Performance')",
            },
          },
          required: ["query"],
        },
        execute: async ({ query, category }: { query: string; category?: string }) => {
          try {
            const { searchSavedGarden } = await import("@/lib/ai/retrieval");
            const data = searchSavedGarden(query, category);
            return {
              content: [
                {
                  type: "text",
                  text: JSON.stringify(data, null, 2),
                },
              ],
            };
          } catch (err: any) {
            return {
              content: [
                {
                  type: "text",
                  text: `Fehler bei der Suche: ${err?.message || "Unbekannter Fehler"}`,
                },
              ],
            };
          }
        },
      },
      {
        name: "get_cheat_sheet_content",
        description:
          "Ruft den sauberen RAG-Textinhalt eines bestimmten Cheat-Sheets anhand seines Slugs ab.",
        inputSchema: {
          type: "object",
          properties: {
            slug: {
              type: "string",
              description:
                "Der Slug der Wissenskachel (z. B. 'web-mcp', 'accessibility', 'git-workflows', 'performance-optimization', 'mdx', 'embeddings', 'ai-landscape', 'intuitive-search')",
            },
          },
          required: ["slug"],
        },
        execute: async ({ slug }: { slug: string }) => {
          try {
            const { savedGardenContent } = await import("@/lib/ai/retrieval");
            const matched = savedGardenContent(slug);
            return {
              content: [
                {
                  type: "text",
                  text: matched
                    ? JSON.stringify(matched, null, 2)
                    : `Keine Kachel für Slug '${slug}' gefunden.`,
                },
              ],
            };
          } catch (err: any) {
            return {
              content: [
                {
                  type: "text",
                  text: `Fehler beim Laden des Inhalts: ${err?.message || "Unbekannt"}`,
                },
              ],
            };
          }
        },
      },
      {
        name: "calculate_wcag_contrast",
        description:
          "Berechnet das WCAG 2.2 Kontrastverhältnis zwischen zwei Hex-Farben und prüft die AA/AAA-Konformität.",
        inputSchema: {
          type: "object",
          properties: {
            foreground: {
              type: "string",
              description: "Vordergrundfarbe als Hex-Code (z. B. '#FFFFFF' oder '#000000')",
            },
            background: {
              type: "string",
              description: "Hintergrundfarbe als Hex-Code (z. B. '#18181B' oder '#F4F4F5')",
            },
          },
          required: ["foreground", "background"],
        },
        execute: async ({ foreground, background }: { foreground: string; background: string }) => {
          try {
            const [r1, g1, b1] = parseHex(foreground);
            const [r2, g2, b2] = parseHex(background);
            const l1 = getRelativeLuminance(r1, g1, b1);
            const l2 = getRelativeLuminance(r2, g2, b2);
            const lighter = Math.max(l1, l2);
            const darker = Math.min(l1, l2);
            const ratio = (lighter + 0.05) / (darker + 0.05);
            const roundedRatio = Math.round(ratio * 100) / 100;

            const result = {
              foreground,
              background,
              contrastRatio: roundedRatio,
              formatted: `${roundedRatio}:1`,
              compliance: {
                wcagAANormalText: roundedRatio >= 4.5,
                wcagAALargeText: roundedRatio >= 3.0,
                wcagAAANormalText: roundedRatio >= 7.0,
                wcagAAALargeText: roundedRatio >= 4.5,
                uiComponents: roundedRatio >= 3.0,
              },
            };

            return {
              content: [
                {
                  type: "text",
                  text: JSON.stringify(result, null, 2),
                },
              ],
            };
          } catch (err: any) {
            return {
              content: [
                {
                  type: "text",
                  text: `Ungültige Farbwerte: ${err?.message || "Fehler bei der Berechnung"}`,
                },
              ],
            };
          }
        },
      },
      {
        name: "navigate_to_garden_tile",
        description:
          "Navigiert den Browser zu einer bestimmten Wissenskachel oder Seite im Digital Garden.",
        inputSchema: {
          type: "object",
          properties: {
            path: {
              type: "string",
              description:
                "Der relative Pfad (z. B. '/garden/accessibility' oder '/garden' oder '/about')",
            },
          },
          required: ["path"],
        },
        execute: async ({ path }: { path: string }) => {
          if (path.startsWith("/")) {
            window.location.href = path;
            return {
              content: [{ type: "text", text: `Navigation zu ${path} ausgelöst.` }],
            };
          }
          return {
            content: [{ type: "text", text: `Ungültiger Pfad: Muss mit '/' beginnen.` }],
          };
        },
      },
    ];

    const controller = new AbortController();

    // Expose tools via official WebMCP API if available
    if (ctx) {
      if (typeof ctx.registerTool === "function") {
        tools.forEach((tool) => {
          try {
            const res = ctx.registerTool(tool, { signal: controller.signal });
            if (res && typeof res.catch === "function") {
              res.catch((err: any) => {
                // Ignore Duplicate tool name and AbortError during Fast Refresh or navigation
                if (err?.name !== "InvalidStateError" && err?.name !== "AbortError") {
                  console.debug?.("WebMCP registration notice:", err?.message || err);
                }
              });
            }
          } catch {
            // Synchronous registration errors
          }
        });
      } else if (typeof ctx.provideContext === "function") {
        try {
          const res = ctx.provideContext({ tools });
          if (res && typeof res.catch === "function") {
            res.catch(() => {});
          }
        } catch {
          // Ignore
        }
      }
    }

    // Also expose on window.__webmcp for testing and simulation
    (window as any).__webmcp_tools = tools;

    return () => {
      controller.abort();
    };
  }, []);

  return null;
}
